import React, { useState } from 'react';
import { plotNotesApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Plus, StickyNote } from 'lucide-react';

const NotesPanel = ({ bookId, notes, setNotes }) => {
    const [showNew, setShowNew] = useState(false);
    const [newNote, setNewNote] = useState({ title: '', content: '', note_type: 'general', labels: [] });
    const [labelInput, setLabelInput] = useState('');

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const response = await plotNotesApi.create({ ...newNote, book_id: bookId });
            setNotes([...notes, response.data]);
            setShowNew(false);
            setNewNote({ title: '', content: '', note_type: 'general', labels: [] });
            toast.success('Note created');
        } catch (error) {
            toast.error('Failed to create note');
        }
    };

    const addLabel = () => {
        if (labelInput.trim() && !newNote.labels.includes(labelInput.trim())) {
            setNewNote({ ...newNote, labels: [...newNote.labels, labelInput.trim()] });
            setLabelInput('');
        }
    };

    const noteTypes = ['general', 'prologue', 'epilogue', 'subplot', 'research'];

    return (
        <div className="p-8">
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-2xl font-semibold">Story Notes</h2>
                <Dialog open={showNew} onOpenChange={setShowNew}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-note-btn">
                            <Plus className="h-4 w-4 mr-2" /> New Note
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>New Note</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreate} className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label>Title</Label>
                                <Input value={newNote.title} onChange={(e) => setNewNote({...newNote, title: e.target.value})} required />
                            </div>
                            <div className="space-y-2">
                                <Label>Type</Label>
                                <select 
                                    className="w-full h-10 rounded-md border border-input bg-background px-3"
                                    value={newNote.note_type}
                                    onChange={(e) => setNewNote({...newNote, note_type: e.target.value})}
                                >
                                    {noteTypes.map(t => (
                                        <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label>Content</Label>
                                <Textarea rows={4} value={newNote.content} onChange={(e) => setNewNote({...newNote, content: e.target.value})} />
                            </div>
                            <div className="space-y-2">
                                <Label>Labels</Label>
                                <div className="flex gap-2">
                                    <Input 
                                        value={labelInput} 
                                        onChange={(e) => setLabelInput(e.target.value)}
                                        placeholder="Add label"
                                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLabel())}
                                    />
                                    <Button type="button" onClick={addLabel}>Add</Button>
                                </div>
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {newNote.labels.map((label, i) => (
                                        <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setNewNote({...newNote, labels: newNote.labels.filter((_, idx) => idx !== i)})}>
                                            {label} ×
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                            <div className="flex justify-end gap-3">
                                <Button type="button" variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
                                <Button type="submit">Create Note</Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {notes.map((note) => (
                    <div key={note.id} className="card-default p-4" data-testid={`note-${note.id}`}>
                        <div className="flex items-start justify-between">
                            <Badge variant="outline">{note.note_type}</Badge>
                        </div>
                        <h3 className="font-serif font-semibold mt-2">{note.title}</h3>
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-4">{note.content}</p>
                        {note.labels?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-3">
                                {note.labels.map((label, i) => (
                                    <Badge key={i} variant="secondary" className="text-xs">{label}</Badge>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            
            {notes.length === 0 && (
                <div className="text-center py-12">
                    <StickyNote className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                    <h3 className="font-serif font-semibold mb-1">No notes yet</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                        Add notes to track ideas, subplots, and research
                    </p>
                    <Button variant="outline" onClick={() => setShowNew(true)}>
                        <Plus className="h-4 w-4 mr-2" /> Add First Note
                    </Button>
                </div>
            )}
        </div>
    );
};

export default NotesPanel;
